import { useState, useEffect, useRef, useCallback } from "react";

const REDUCE =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const prefersReduced = REDUCE;

// Smooth-scroll helper that cooperates with Lenis when active.
export function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: 0, duration: 1.2 });
  } else {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

export function scrollTop() {
  if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.2 });
  else window.scrollTo({ top: 0, behavior: "smooth" });
}

// ---- Theme (default dark, persisted) --------------------------------------
export function useTheme() {
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("lb-theme") || "dark";
    } catch {
      return "dark";
    }
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem("lb-theme", theme);
    } catch {}
  }, [theme]);

  const toggle = useCallback(
    () => setTheme((t) => (t === "dark" ? "light" : "dark")),
    []
  );
  return { theme, toggle };
}

// ---- Language --------------------------------------------------------------
export function useLang() {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("lb-lang") || "fr";
    } catch {
      return "fr";
    }
  });
  useEffect(() => {
    document.documentElement.lang = lang;
    try {
      localStorage.setItem("lb-lang", lang);
    } catch {}
  }, [lang]);
  const toggle = useCallback(
    () => setLang((l) => (l === "fr" ? "en" : "fr")),
    []
  );
  return { lang, toggle };
}

// ---- Animated counter ------------------------------------------------------
export function useCounter(target, { duration = 1100 } = {}) {
  const [value, setValue] = useState(REDUCE ? target : 0);
  const ref = useRef(null);
  const done = useRef(false);

  useEffect(() => {
    if (REDUCE) {
      setValue(target);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !done.current) {
            done.current = true;
            const start = performance.now();
            const tick = (now) => {
              const p = Math.min((now - start) / duration, 1);
              const eased = 1 - Math.pow(1 - p, 3); // cubic-out
              setValue(Math.round(target * eased));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, duration]);

  return { ref, value };
}


// ---- Per-page noindex/nofollow (private routes) ----------------------------
export function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);
}
